import NiceModal, { useModal } from '@ebay/nice-modal-react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Link as MuiLink,
  TextField,
  Typography
} from '@mui/material'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import { useDeleteCompanyMutation } from '../../hooks/useMutations/useCompaniesMutations'
import { useSnackbar } from '../../hooks/useSnackbar'
import { LoaderButton } from '../LoaderButton'

export const DeleteCompanyModal = NiceModal.create(
  ({ companyId, companyName }: { companyId: string; companyName: string }) => {
    const modal = useModal()
    const { t } = useTranslation()
    const [confirmationText, setConfirmationText] = useState('')
    const { showSnackbar } = useSnackbar()
    const navigate = useNavigate()
    const mutation = useDeleteCompanyMutation()

    return (
      <Dialog
        open={modal.visible}
        onClose={() => modal.remove()}
        sx={{
          '& .MuiDialog-paper': {
            width: '37.5rem',
            padding: '1.5rem'
          }
        }}
      >
        <DialogTitle sx={{ padding: '1rem 1.5rem' }}>
          <Typography fontWeight='bold' variant='h6' component='span'>
            {t('delete_company_modal.title')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: '1.5rem' }}>
          <Typography variant='body1' sx={{ my: '1rem' }}>
            <Trans
              i18nKey='delete_company_modal.explanation'
              components={[
                <MuiLink
                  component={Link}
                  to='/terms'
                  target='_blank'
                  rel='noopener noreferrer'
                  underline='hover'
                  display='inline'
                />
              ]}
            />
          </Typography>
          <Typography variant='body1'>
            {t('delete_company_modal.confirmation_explanation')}
          </Typography>
          <FormControl fullWidth sx={{ py: '1rem' }}>
            <TextField
              value={confirmationText}
              onChange={e => setConfirmationText(e.target.value)}
              placeholder={t('delete_company_modal.confirmation_text')}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
            ></TextField>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ padding: '.5rem' }}>
          <Button
            variant='outlined'
            color='primary'
            onClick={() => modal.remove()}
            sx={{
              textTransform: 'capitalize',
              backgroundColor: 'rgba(178, 223, 219, 0.10)'
            }}
          >
            {t('common.cancel')}
          </Button>
          <LoaderButton
            variant='contained'
            label={t('delete_company_modal.delete')}
            loading={mutation.isPending}
            disabled={
              !confirmationText ||
              confirmationText !==
                t('delete_company_modal.confirmation_text') ||
              mutation.isPending
            }
            sx={{
              textTransform: 'capitalize',
              backgroundColor: 'error.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'error.dark'
              }
            }}
            onClick={() =>
              mutation.mutate(companyId, {
                onSuccess: () => {
                  showSnackbar({
                    message: t('delete_company_modal.success', {
                      companyName
                    }),
                    severity: 'success'
                  })
                  navigate('/companies')
                },
                onError: () => {
                  showSnackbar({
                    message: t('delete_company_modal.error', { companyName }),
                    severity: 'error'
                  })
                },
                onSettled: () => {
                  setConfirmationText('')
                  modal.remove()
                }
              })
            }
          />
        </DialogActions>
      </Dialog>
    )
  }
)
